import { cn } from "@/lib/utils/cn";

export type SpinnerSize = "sm" | "md" | "lg";

const SIZE_CLASSES: Record<SpinnerSize, string> = {
  sm: "size-3.5",
  md: "size-5",
  lg: "size-8",
};

/**
 * The same sizes as INTRINSIC SVG attributes, in px.
 *
 * Belt and braces, and the braces matter. An `<svg>` that carries a viewBox but no width/height
 * attribute has no intrinsic size: if its sizing class does not arrive — a stylesheet that 404s,
 * a purge that drops the utility, a CDN serving a stale bundle — the element does not fall back
 * to something small, it expands to fill its container. A spinner then becomes a circle the size
 * of the viewport, which is exactly the failure this pair of attributes makes impossible.
 *
 * The class still wins whenever CSS loads, because a CSS width overrides the presentation
 * attribute. These are the floor, not the answer.
 */
const SIZE_PX: Record<SpinnerSize, number> = {
  sm: 14,
  md: 20,
  lg: 32,
};

export interface SpinnerProps {
  size?: SpinnerSize;
  /** Announced to screen readers; omit when an adjacent label already says it. */
  label?: string;
  className?: string;
}

export function Spinner({ size = "md", label, className }: SpinnerProps) {
  const icon = (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      width={SIZE_PX[size]}
      height={SIZE_PX[size]}
      aria-hidden="true"
      className={cn("animate-spin", SIZE_CLASSES[size], label ? undefined : className)}
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="2.5"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );

  if (!label) return icon;

  return (
    <span role="status" className={cn("inline-flex items-center gap-2", className)}>
      {icon}
      <span className="sr-only">{label}</span>
    </span>
  );
}
